import {
  DOMNode,
  Element,
  Text,
  HTMLReactParserOptions,
  domToReact,
  attributesToProps,
} from "html-react-parser"; // highlight-line
import parse from "html-react-parser";
import Link from "next/link"; // highlight-line
import { CodeHighlight, highlightLanguages } from "./CodeHighlight";
import Tabs from "./wysiwyg/Tabs";
import classNames from "classnames";
import { NodeWithChildren } from "domhandler";
import { StatusMessage } from "./wysiwyg/StatusMessage";
import { ModalImage } from "./wysiwyg/ModalImage";
import { useEffect } from "react";

interface nodeProps {
  type: string;
  name: string;
}

const DynamicComponents = [Tabs];

// Mention class names used in CKeditor here so they are rendered into CSS.
("!text-white hover:bg-white hover:!text-pink-600 hover:!text-orange-600 hover:!text-teal-600 hover:!text-navy-600");
("shadow hover:shadow-pink-500/50  hover:shadow-orange-500/50  hover:shadow-teal-500/50  hover:shadow-navy-500/50");
("rounded-xl p-4 flex-grow shadow font-bold  text-lg text-navy-600 bg-white pl-20 hover:shadow-inner no-underline");
("inline-block");

const options: HTMLReactParserOptions = {
  replace: (domNode: DOMNode) => {

    if (!(domNode instanceof Element)) {
      return;
    }

    // Convert images to modal pop-ups so they can be enlarged.
    if (domNode.tagName == "img") {
      return <ModalImage {...attributesToProps(domNode.attribs)} />
    }

    // Convert <a> tags into Next.js Link elements.
    if (domNode.tagName === "a") {
      const { href, class: className, target, id } = domNode.attribs;

      // Remove headerlinks from migration.
      if (className == "headerlink") {
        return <></>;
      }
      
      let newHref = href

      if(!href && id) {
        newHref = '#' + id;
      }

      return (
        <Link {...attributesToProps(domNode.attribs)} href={newHref} className={className} target={target}>
          {domToReact(domNode.children)}
        </Link>
      );
    }

    // Removing old table of content from migration.
    if (domNode.tagName === "nav" && domNode.attribs.id === "contents") {
      return <></>;
    }

    // Set IDs and permalinks on headings.
    if (
      domNode.tagName === "h2" ||
      domNode.tagName === "h3" ||
      domNode.tagName === "h4"
    ) {
      if (!domNode.attribs.id?.length) {
        domNode.attribs.id = asId(extractText(domNode.children));
      }
      return (
        <domNode.tagName
          {...domNode.attribs}
          className={classNames(domNode.attribs.className, "group")}
        >
          {domToReact(domNode.children, options)}
          <a
            href={`#${domNode.attribs.id}`}
            className="ref hidden group-hover:inline pl-1"
            title="Permalink to this heading"
          >
            ¶
          </a>
        </domNode.tagName>
      );
    }

    // Scrap out tabs widgets.
    if (domNode.tagName == "div") {
      if (domNode.attribs["data-component-type"] !== undefined) {
        const props =
          domNode.attribs["data-component"] !== undefined
            ? JSON.parse(domNode.attribs["data-component"])
            : {};
        switch (domNode.attribs["data-component-type"]) {
          case "Tabs":
            return <Tabs {...props} />;
          case "StatusMessage":
            return (
              <StatusMessage {...props}>
                {domToReact(domNode.children, options)}
              </StatusMessage>
            );
        }
      }
      // Migrated tabs
      else if (
        domNode.attribs.class != undefined &&
        domNode.attribs.class.indexOf("sphinx-tabs ") !== -1
      ) {
        const tabList = domNode.children.filter(
          (e) => e instanceof Element && e.attribs.role == "tablist"
        );
        if (!tabList.length) {
          return;
        }
        const tabListEl = tabList[0];
        if (!(tabListEl instanceof Element)) {
          return;
        }
        const tabLabels = tabListEl.children
          .map((c) => (c instanceof Element ? c.children[0] : []))
          .map((d) => (d instanceof Text ? d.data : ""));

        const tabContentList = domNode.children.filter(
          (e) => e instanceof Element && e.attribs.role == "tabpanel"
        );
        if (!tabContentList.length) {
          return;
        }

        const tabs = tabLabels.map((label, i) => {
          const node =
            tabContentList[i] instanceof Element ? tabContentList[i] : null;
          if (node instanceof Element) {
            return {
              label,
              node: domToReact(node.children, options),
            };
          }
          return {
            label,
            content: { processed: "" },
          };
        });
        return <Tabs tabs={tabs} />;
      }
    }

    if (domNode.tagName  == "table") {
      return (
        <div className="table-overflow-auto">
          <domNode.tagName {...attributesToProps(domNode.attribs)}>
                {domToReact(domNode.children, options)}
          </domNode.tagName>
        </div>
      )
    }
  
    // Add syntax highlighting to 'pre > code' elements with a language class set.
    if (
      domNode.tagName === "code" &&
      domNode.parentNode instanceof Element &&
      domNode.parentNode.name === "pre"
    ) {
      const languages = domNode.attribs.class
        .split(" ")
        .filter((className) => className.indexOf("language-") === 0);
      if (!languages.length) {
        return;
      }

      // We can only syntax highlight text nodes. If there are Elements in the children set. We can't
      // render it.
      const textNodes: Text[] = domNode.children.filter(
        (node): node is Text => node instanceof Text
      );
      if (textNodes.length < domNode.children.length) {
        return;
      }

      const lang = languages[0].replace(
        "language-",
        ""
      ) as keyof typeof highlightLanguages;
      const code = textNodes.map((n) => n.data).join("\n");

      return <CodeHighlight language={lang} code={code} />;
    }
  },
};

interface BodyProps extends React.ComponentPropsWithoutRef<"div"> {
  value: string;
  sectionScroll?: boolean;
}

export function Body({ value, ...props }: BodyProps) {
  const scrollToHash = (hash:string) => {
      // For scrolling to a particular section in a page
      document.querySelector(hash)?.scrollIntoView({behavior:"smooth"});
  }

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && props.sectionScroll)
    {
      scrollToHash(hash)
    }
  }, [props.sectionScroll])
  
  return (
    <div className={classNames("ck", props.className ?? "")}>
      {parse(value, options)}
    </div>
  );
}

function extractText(children: NodeWithChildren["children"]): string {
  const text = children.map((el) => {
    if (el instanceof Element) {
      return extractText(el.children);
    }
    if (el instanceof Text) {
      return el.data;
    }
    return "";
  });

  return text.join("");
}

function asId(text: string) {
  return (
    "section-" +
    text
      .toLowerCase()
      .replaceAll(" ", "-")
      .replaceAll(/[^a-z0-9\-]/g, "")
  );
}
